import React, { useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getDashboardRoute } from "../../utils/roleUtils";

export default function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [loading,      setLoading]      = useState(false);
  const [loginError,   setLoginError]   = useState("");
  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [remember,     setRemember]     = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Preserved exactly — called with {email, password} from handleSubmit
  const handleLogin = async (values) => {
    setLoginError("");
    setLoading(true);
    try {
      const data = await login(values.email, values.password);
      const decoded = jwtDecode(data.access);
      navigate(getDashboardRoute(decoded.role), { replace: true });
    } catch (err) {
      const data = err?.response?.data;
      if (data?.error === "inactive") {
        setLoginError(data.detail);
      } else if (data?.error === "invalid_credentials") {
        setLoginError(data.detail);
      } else if (data?.detail) {
        setLoginError(data.detail);
      } else {
        setLoginError("Unable to connect. Please check your connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin({ email, password });
  };

  const labelStyle = {
    display: "block",
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
    marginBottom: 6,
  };

  const inputStyle = {
    width: "100%",
    height: 44,
    padding: "0 12px",
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    fontSize: 15,
    boxSizing: "border-box",
    outline: "none",
  };

  return (
    <div style={{
      minHeight: "100vh",
      backgroundColor: "#F0F2F5",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px 16px",
    }}>
      <style>{`
        .dhn-input:focus { border-color: #2563EB !important; }
        .dhn-signin-btn:hover:not(:disabled) { background: #1D4ED8 !important; }
      `}</style>

      <div style={{
        background: "white",
        borderRadius: 12,
        boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
        width: 400,
        padding: 40,
        boxSizing: "border-box",
      }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <img
            src="/mchusa_dhn.png"
            alt="MCHUSA DHN"
            style={{ display: "block", width: 200, margin: "0 auto" }}
          />
        </div>

        <form onSubmit={handleSubmit}>

          {/* Email */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Email address</label>
            <input
              className="dhn-input"
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); if (loginError) setLoginError(""); }}
              style={inputStyle}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              required
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                className="dhn-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => { setPassword(e.target.value); if (loginError) setLoginError(""); }}
                style={{ ...inputStyle, paddingRight: 44 }}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#9CA3AF",
                  fontSize: 13,
                  padding: 0,
                }}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {/* Remember me */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
            <input
              type="checkbox"
              id="remember"
              checked={remember}
              onChange={e => setRemember(e.target.checked)}
            />
            <label htmlFor="remember" style={{ fontSize: 14, color: "#6B7280", cursor: "pointer" }}>
              Remember me
            </label>
          </div>

          {/* Error */}
          {loginError && (
            <div style={{
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: 8,
              padding: "10px 14px",
              color: "#DC2626",
              fontSize: 14,
              marginBottom: 16,
            }}>
              {loginError}
            </div>
          )}

          {/* Sign in button */}
          <button
            className="dhn-signin-btn"
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              height: 48,
              background: "#2563EB",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: loading ? "default" : "pointer",
              opacity: loading ? 0.7 : 1,
              marginBottom: 20,
              transition: "background 0.15s",
            }}
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

        </form>

        {/* Divider */}
        <hr style={{ border: "none", borderTop: "1px solid #E5E7EB", margin: "4px 0 16px 0" }} />

        {/* Forgot password */}
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          <span style={{ color: "#6B7280", fontSize: 14 }}>Forgot your password? </span>
          <a href="#" style={{ color: "#2563EB", fontSize: 14, textDecoration: "none" }}>
            Reset it
          </a>
        </div>

        {/* Sign up */}
        <div style={{ textAlign: "center" }}>
          <span style={{ color: "#6B7280", fontSize: 14 }}>New to DHN? </span>
          <a
            href="#"
            onClick={e => { e.preventDefault(); navigate("/signup"); }}
            style={{ color: "#2563EB", fontSize: 14, textDecoration: "none", fontWeight: 600 }}
          >
            Create an account
          </a>
        </div>

      </div>

      {/* Copyright — outside the card */}
      <p style={{ textAlign: "center", color: "#9CA3AF", fontSize: 12, marginTop: 24 }}>
        © {new Date().getFullYear()} MCHUSA — HIPAA-Compliant Healthcare Platform
      </p>
    </div>
  );
}
